/*
 * MediaHubWidgetPromoCard.jsx
 * ───────────────────────────
 * Persistent, discoverable card (NOT a one-time interrupt popup — see
 * ShowOnLockScreenButton for that pattern instead) that tells athletes
 * about the Media Hub Home Screen widget and walks them through adding
 * it themselves.
 *
 * Important: unlike ShowOnLockScreenButton (which starts a Live
 * Activity via a real native API call), there is NO iOS API for an
 * app to add a widget to the Home Screen on the user's behalf — Apple
 * doesn't expose one to any app, ours included. So this card can only
 * educate + walk through the manual steps (long-press → tap + →
 * search FLYT → add), never do it automatically. iOS-only (widgets
 * don't exist on Android/web), so it's gated on isNativeApp() +
 * platform() === 'ios'.
 *
 * The widget itself is zero-configuration — no slug to type in. See
 * setMediaHubWidgetAthlete() (nativeBridge.js), which this page's
 * data-load effect calls so the widget always shows the logged-in
 * athlete's own account (never one someone typed into "Edit Widget").
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight } from 'lucide-react';
import { isNativeApp, platform, haptic } from '../../lib/nativeBridge';
import { FlytWings } from '../FlytWings';

const STORAGE_KEY = 'flyt_widget_promo_dismissed_v1';

function isDismissed() {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}
function setDismissed() {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
}

export function MediaHubWidgetPromoCard() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(true);

  // iter · Read localStorage only after mount — avoids a server/client
  // hydration mismatch and keeps the very first paint simple.
  useEffect(() => {
    setHidden(!isNativeApp() || platform() !== 'ios' || isDismissed());
  }, []);

  if (hidden) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => { haptic('light'); setOpen(true); }}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl ring-1 ring-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition text-left"
        data-testid="media-hub-widget-promo-card"
      >
        <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center shrink-0 ring-1 ring-white/10">
          <FlytWings className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-bold text-sm leading-tight">Get the Media Hub widget</p>
          <p className="text-white/50 text-xs leading-snug truncate">See new photos & videos right on your Home Screen</p>
        </div>
        <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
      </button>
      {open && (
        <WidgetPromoSheet
          onClose={() => setOpen(false)}
          onDontShowAgain={() => { setDismissed(); setOpen(false); setHidden(true); }}
        />
      )}
    </>
  );
}

function WidgetPromoSheet({ onClose, onDontShowAgain }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const steps = [
    { n: 1, text: 'Long-press an empty spot on your Home Screen' },
    { n: 2, text: 'Tap the + in the top corner' },
    { n: 3, text: 'Search "FLYT" → pick Media Hub → Add Widget — it already knows it’s you, no typing needed' },
  ];

  const sheet = (
    <div
      className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="media-hub-widget-promo-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-10 ring-1 ring-white/10 sm:mb-8"
        style={{ background: 'linear-gradient(180deg, rgba(20,22,26,0.98) 0%, rgba(6,8,12,1) 100%)' }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-widest mb-1">Home Screen widget</p>
            <h3 className="text-white text-xl font-black leading-tight">See your Media Hub, live</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
            aria-label="Close"
            data-testid="media-hub-widget-promo-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-white/70 text-sm mb-5 leading-relaxed">
          Add the Media Hub widget to your Home Screen to see photos and
          videos flow in from parents, photographers, and fans — no need
          to open the app.
        </p>

        <WidgetPreview />

        <div className="space-y-3 my-5">
          {steps.map((s) => (
            <div key={s.n} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-white/10 text-white/80 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                {s.n}
              </div>
              <p className="text-white/80 text-sm leading-snug flex-1">{s.text}</p>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full py-3.5 rounded-2xl bg-white text-black font-black text-sm tracking-wide active:scale-[0.98] transition"
          data-testid="media-hub-widget-promo-gotit"
        >
          Got it!
        </button>
        <button
          type="button"
          onClick={onDontShowAgain}
          className="w-full mt-3 py-2.5 text-white/50 font-semibold text-sm hover:text-white transition"
          data-testid="media-hub-widget-promo-dontshow"
        >
          Don&rsquo;t show this again
        </button>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
}

// Lightweight illustrative preview of the medium widget — not wired to
// real data (this is a promo, not the widget itself), just enough to
// show what it looks like on a Home Screen before someone commits to
// adding it for real.
function WidgetPreview() {
  const rows = [
    { label: 'PARENT', color: '#33CC72' },
    { label: 'PHOTOG', color: '#FFFFFF' },
    { label: 'FAN', color: '#F04545' },
  ];
  return (
    <div className="rounded-2xl p-3.5 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #000000, #0F1410 130%)' }}>
      <div className="flex-1 min-w-0 space-y-2">
        <p className="text-white/35 text-[8px] font-black tracking-[0.15em]">FLYT</p>
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-1.5">
            <span className="text-white/55 text-[8px] font-black tracking-wide w-12 shrink-0">{r.label}</span>
            <span className="h-[2px] flex-1 rounded-full" style={{ background: r.color }} />
          </div>
        ))}
      </div>
      <div className="w-11 h-11 rounded-xl bg-white/[0.06] ring-1 ring-white/15 flex items-center justify-center shrink-0">
        <FlytWings className="w-5 h-5" />
      </div>
    </div>
  );
}

export default MediaHubWidgetPromoCard;
